import { ITrackingInfo, ORDER_SHIPMENT_STATUS, ORDER_STATUS, SHIPMENT_STATUS, TShipmentDestinationPlace } from 'types/orders';
import { formatShortDate } from 'utils/formatDatesAndHours';
import BordOneToneIcon from 'components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import { getDestinationDisplayName } from '../../trackingOrder.utils';
import './ShipmentItem.scss';

interface IShipmentItem {
  receiverName?: string;
  destinationPlace?: TShipmentDestinationPlace;
  countryName?: string;
  currentShipmentNumber?: string | number;
  totalShipmentsNumber?: string | number;
  shipmentNumber?: string | number;
  trackingInfo?: ITrackingInfo;
  status: ORDER_SHIPMENT_STATUS | SHIPMENT_STATUS | ORDER_STATUS; // unificar cuando esté listo el back
  onClick?: () => void;
  onPreparationClick?: () => void;
}

/**
 * Componente que representa un ítem de envío dentro de una orden.
 * Muestra información resumida del destino, número de envío y ETA.
 * Soporta click para abrir el detalle del envío.
 */
const ShipmentItem = ({
  receiverName,
  destinationPlace,
  countryName,
  currentShipmentNumber,
  totalShipmentsNumber,
  shipmentNumber,
  trackingInfo,
  status,
  onClick,
  onPreparationClick
}: IShipmentItem) => {
  const destinationName = getDestinationDisplayName(destinationPlace, receiverName, countryName);
  const isConfirmed = status === ORDER_SHIPMENT_STATUS.CONFIRMED || status === ORDER_STATUS.PENDING; // unificar cuando esté listo el back
  const isPreparation = status === ORDER_SHIPMENT_STATUS.PREPARATION || status === ORDER_STATUS.ACTIVE; // unificar cuando esté listo el back
  const isInWarehouse = status === ORDER_SHIPMENT_STATUS.IN_WAREHOUSE;
  const isTransitToDestination = status === ORDER_SHIPMENT_STATUS.TRANSIT_TO_DESTINATION;
  const isDelivered = status === ORDER_SHIPMENT_STATUS.DELIVERED || status === ORDER_STATUS.DELIVERED; // unificar cuando esté listo el back
  const shouldRenderShipmentSection = !isConfirmed;
  const shouldRenderDateSection = !isConfirmed && !isPreparation;

  /**
   * Determina si la fecha estimada de entrega ya pasó.
   * Solo aplica cuando el envío está en tránsito a destino final.
   */
  const isEtaOverdue = (() => {
    if (!isTransitToDestination || !trackingInfo?.currentEstimatedDeliverDate) return false;
    const etaDate = new Date(trackingInfo.currentEstimatedDeliverDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return etaDate < today;
  })();

  /**
   * Obtiene la fecha formateada a mostrar según el estado del envío.
   * Si está entregado muestra deliveryDate, sino muestra currentEstimatedDeliverDate (ETA).
   */
  const getDisplayDate = (): string => {
    const dateToFormat = isDelivered
      ? trackingInfo?.deliveryDate
      : trackingInfo?.currentEstimatedDeliverDate;
    return dateToFormat ? formatShortDate(dateToFormat)?.onlyDate || 'Sin fecha' : 'Sin fecha';
  };

  /**
   * Obtiene el label de la fecha según el estado del envío.
   */
  const getDateLabel = (): string => {
    if (isDelivered) return 'Entregado';
    if (isInWarehouse) return 'Llegó';
    return 'ETA';
  };

  const handleClick = () => onClick?.();
  const isClickable = Boolean(onClick);
  const itemClassName = ['shipmentItem', isClickable && 'clickable', isEtaOverdue && 'etaOverdue']
    .filter(Boolean)
    .join(' ');

  /**
   * Maneja el click en el botón de checklist de preparación.
   * Previene la propagación para evitar que se dispare el onClick del contenedor.
   * @param e - Evento del mouse
   */
  const handlePreparationClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPreparationClick?.();
  };

  return (
    <div className={itemClassName} onClick={handleClick} role={isClickable ? 'button' : undefined}>
      <span className="destinationName">
        {destinationName}{' '}
        <b>
          {currentShipmentNumber}/{totalShipmentsNumber}
        </b>
      </span>
      {shouldRenderShipmentSection && (
        <>
          {isPreparation ? (
            <div className="shipmentNumberWrapper">
              <button onClick={handlePreparationClick} aria-label="Ver checklist de preparación">
                <BordOneToneIcon variant="rightToLeftListBullet" standardSize={10} customWidth="11px" />
              </button>
              <span className="shipmentNumber">(1/5)</span>{' '}
              {/* TODO: cambiar por el número real cuando esté listo el back*/}
            </div>
          ) : (
            <span className="shipmentNumber">Envío {shipmentNumber}</span>
          )}
        </>
      )}
      {shouldRenderDateSection && (
        <span className="etaDate">
          <b>{getDateLabel()}:</b> {getDisplayDate()}
        </span>
      )}
    </div>
  );
};

export default ShipmentItem;
